import React, { useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { CheckCircle, ExpandMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  HomePageToolsDetails,
  categoryDescriptions,
} from "../Constants/tools_details";
import HappyUsersCounter from "../Components/HappyUsersCount";
import FlipCard from "../Components/FlipCard";
import { motion } from "framer-motion";

const features = [
  "No software installation required",
  "Works directly in your browser",
  "100% secure - we don't store your input data",
  "Instant results with one click",
  "Free to try with no commitment",
  "Regular updates with new features",
];

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const toolsSectionRef = useRef(null);

  // Group tools by category
  const groupedTools = {};
  HomePageToolsDetails.forEach((tool) => {
    const category = tool.category || "Misc";
    if (!groupedTools[category]) {
      groupedTools[category] = [];
    }
    groupedTools[category].push(tool);
  });

  // Define ordered categories from categoryDescriptions (excluding 'default')
  const orderedCategories = Object.keys(categoryDescriptions).filter(
    (key) => key !== "default"
  );

  // Get extra categories (not in categoryDescriptions) and sort them
  const extraCategories = Object.keys(groupedTools)
    .filter((category) => !orderedCategories.includes(category))
    .sort((a, b) => a.localeCompare(b));

  // Combine ordered and extra categories
  const allCategories = [...orderedCategories, ...extraCategories];

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 10,
          position: "relative",
          overflow: "hidden",
          "&:after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "100px",
            background: `linear-gradient(to bottom right, transparent 49%, ${theme.palette.background.default} 50%)`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 700 }}
              >
                Handpicked Tools. Real Results!
              </Typography>
              <Typography
                variant="h5"
                component="p"
                gutterBottom
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Explore a curated collection of time-saving tools built for
                creators, developers, and doers. No fluff — just real utility.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#FFC107",
                    color: "black",
                    borderRadius: "20px",
                    textTransform: "none",
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#e0a800",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "scale(0.97)",
                    },
                  }}
                  onClick={() => {
                    navigate("/welcome/signup");
                  }}
                >
                  Try Tools Free
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <HappyUsersCounter />

      {/* Tools Section */}
      <Container maxWidth="xl" sx={{ py: 8 }} ref={toolsSectionRef}>
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700 }}
        >
          Powerful Tools by Category
        </Typography>
        <Typography
          variant="h6"
          component="p"
          align="center"
          color="text.secondary"
          sx={{ maxWidth: 700, mx: "auto", mb: 6 }}
        >
          Grow faster with better data and smarter tools — neatly organized by
          category.
        </Typography>

        {allCategories
          .filter((category) => groupedTools[category]?.length > 0)
          .map((category) => (
            <Accordion
              key={category}
              defaultExpanded
              sx={{
                mb: 5,
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${theme.palette.grey[300]}`,
                transition: "box-shadow 0.3s ease, transform 0.3s ease",
                "&:hover": {
                  boxShadow: `0 6px 16px ${theme.palette.grey[400]}`,
                  transform: "translateY(-2px)",
                },
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  position: "relative",
                  minHeight: 56,
                  "&.Mui-expanded": {
                    minHeight: 56,
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: 0,
                    justifyContent: "center",
                    "&.Mui-expanded": {
                      margin: 0,
                    },
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    position: "absolute",
                    right: 8,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  {category}
                </Typography>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  px: 1, // adds minimal side padding
                  py: 2,
                  pb: 4,
                }}
              >
                <Grid container spacing={8} justifyContent="center">
                  {groupedTools[category].map((tool, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      md={4}
                      key={index}
                      sx={{ px: 1 }}
                      display="flex"
                      justifyContent="center"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        style={{ width: "100%" }}
                      >
                        <FlipCard tool={tool} />
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: "background.paper", py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              Why Choose Our Tools
            </Typography>

            <List sx={{ textAlign: "left", pl: 10, mx: "auto", maxWidth: 600 }}>
              {features.map((feature, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircle color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Paper
          elevation={3}
          sx={{
            p: 6,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: "primary.contrastText",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            Ready to Boost Your Business Success?
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#FFC107",
                color: "black",
                borderRadius: "20px",
                textTransform: "none",
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#e0a800",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "scale(0.97)",
                },
              }}
              onClick={() => {
                window.location.href = "/welcome/signup";
              }}
            >
              Get Started Free
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Home;
